import { useState, useEffect } from 'react';
import { Loader2, Search, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;

const ENTITIES = [
  { id: 'bi_immo', label: 'Bonjour Immobilier' },
  { id: 'afpv', label: 'AFPV Invest' },
];

function fmtEUR(v) {
  if (v == null || v === '') return '-';
  return `\u20ac${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function PennylaneInvoicePreviewPanel({ entityId: externalEntityId }) {
  const [entityId, setEntityId] = useState(externalEntityId || 'bi_immo');
  const [year, setYear] = useState(currentMonth > 1 ? currentYear : currentYear - 1);
  const [month, setMonth] = useState(currentMonth > 1 ? currentMonth - 1 : 12);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedChannels, setExpandedChannels] = useState(new Set());

  useEffect(() => {
    if (externalEntityId && externalEntityId !== entityId) {
      setEntityId(externalEntityId);
      setResult(null);
    }
  }, [externalEntityId]);

  const toggleChannel = (ch) => {
    setExpandedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(ch)) next.delete(ch);
      else next.add(ch);
      return next;
    });
  };

  const runPreview = async () => {
    setLoading(true);
    setResult(null);
    setExpandedChannels(new Set());
    try {
      const { data } = await diagnosticsAPI.pennylaneInvoicePreview({
        entity_id: entityId,
        year,
        month,
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const hasMismatches = result?.net_mismatch_count > 0;
  const hasUnassigned = result?.unassigned_property_count > 0;
  const hasNotRoutable = (result?.not_routable?.count ?? 0) > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-5 w-5 text-blue-500" />
          FR Invoice Preview (Pennylane)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Read-only preview of bookings that would be invoiced. No writes, no Pennylane API calls.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Controls */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Entity</label>
            <select
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {ENTITIES.map((ent) => (
                <option key={ent.id} value={ent.id}>{ent.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Year</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => currentYear - 2 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Month</label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {new Date(2024, m - 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <Button onClick={runPreview} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>
        </div>

        {/* Results */}
        {result && (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Invoiceable bookings</p>
                <p className="text-xl font-bold tabular-nums">{result.invoiceable_booking_count}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Net revenue</p>
                <p className="text-xl font-bold tabular-nums">{fmtEUR(result.totals?.net_revenue)}</p>
              </div>
              <div className={`rounded-lg border p-3 ${hasMismatches ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950' : ''}`}>
                <p className="text-xs text-muted-foreground">Line-sum mismatches</p>
                <p className={`text-xl font-bold tabular-nums ${hasMismatches ? 'text-red-600 dark:text-red-400' : ''}`}>
                  {result.net_mismatch_count}
                </p>
              </div>
              <div className={`rounded-lg border p-3 ${hasUnassigned ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950' : ''}`}>
                <p className="text-xs text-muted-foreground">Unassigned properties</p>
                <p className={`text-xl font-bold tabular-nums ${hasUnassigned ? 'text-amber-600 dark:text-amber-400' : ''}`}>
                  {result.unassigned_property_count}
                </p>
              </div>
              <div className={`rounded-lg border p-3 ${hasNotRoutable ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950' : ''}`}>
                <p className="text-xs text-muted-foreground">Not routable</p>
                <p className={`text-xl font-bold tabular-nums ${hasNotRoutable ? 'text-red-600 dark:text-red-400' : ''}`}>
                  {result.not_routable?.count ?? 0}
                </p>
              </div>
            </div>

            {/* Excluded counts */}
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span>Excluded: {result.excluded?.cancelled ?? 0} cancelled</span>
              <span>{result.excluded?.quarantined ?? 0} quarantined</span>
              <span>{result.excluded?.already_invoiced ?? 0} already invoiced</span>
            </div>

            {/* VAT note */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950 p-2">
              <p className="text-xs text-muted-foreground">{result.vat_note}</p>
            </div>

            {/* Channel groups */}
            {result.channel_groups?.map((group) => (
              <div key={group.channel} className="rounded-lg border">
                {/* Channel header (clickable) */}
                <button
                  onClick={() => toggleChannel(group.channel)}
                  className="w-full flex items-center justify-between p-3 hover:bg-muted/30 transition-colors text-left"
                >
                  <div className="flex items-center gap-2">
                    {expandedChannels.has(group.channel) ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                    <Badge variant="outline">{group.channel}</Badge>
                    <span className="text-sm text-muted-foreground">
                      {group.booking_count} booking{group.booking_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <span className="text-sm font-medium tabular-nums">
                    Net: {fmtEUR(group.subtotals?.net_revenue)}
                  </span>
                </button>

                {/* Channel subtotals */}
                {expandedChannels.has(group.channel) && (
                  <div className="border-t">
                    <div className="grid grid-cols-4 gap-2 p-3 bg-muted/20 text-xs">
                      <div>
                        <span className="text-muted-foreground">Accommodation: </span>
                        <span className="font-medium tabular-nums">{fmtEUR(group.subtotals?.accommodation)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Cleaning: </span>
                        <span className="font-medium tabular-nums">{fmtEUR(group.subtotals?.cleaning_fee)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Commission: </span>
                        <span className="font-medium tabular-nums">{fmtEUR(group.subtotals?.commission_deduction)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Net: </span>
                        <span className="font-medium tabular-nums">{fmtEUR(group.subtotals?.net_revenue)}</span>
                      </div>
                    </div>

                    {/* Bookings table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/30">
                            <th className="text-left px-2 py-1 font-medium">ID</th>
                            <th className="text-left px-2 py-1 font-medium">Property</th>
                            <th className="text-left px-2 py-1 font-medium">Guest</th>
                            <th className="text-left px-2 py-1 font-medium">Check-in</th>
                            <th className="text-right px-2 py-1 font-medium">Accomm.</th>
                            <th className="text-right px-2 py-1 font-medium">Cleaning</th>
                            <th className="text-right px-2 py-1 font-medium">Commission</th>
                            <th className="text-right px-2 py-1 font-medium">Net</th>
                            <th className="text-center px-2 py-1 font-medium">Check</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.bookings.map((bk) => (
                            <tr
                              key={bk.uplisting_id}
                              className={`border-b ${bk.net_mismatch ? 'bg-red-50 dark:bg-red-950/30' : ''}`}
                            >
                              <td className="px-2 py-1 font-mono">{bk.uplisting_id}</td>
                              <td className="px-2 py-1 max-w-[160px] truncate" title={bk.property_name}>
                                {bk.property_name}
                              </td>
                              <td className="px-2 py-1 max-w-[120px] truncate">{bk.guest_name}</td>
                              <td className="px-2 py-1">{bk.check_in}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{fmtEUR(bk.lines?.accommodation)}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{fmtEUR(bk.lines?.cleaning_fee)}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{fmtEUR(bk.lines?.commission_deduction)}</td>
                              <td className="px-2 py-1 text-right tabular-nums font-medium">{fmtEUR(bk.net_revenue)}</td>
                              <td className="px-2 py-1 text-center">
                                {bk.net_mismatch ? (
                                  <AlertTriangle className="h-3.5 w-3.5 text-red-500 inline" />
                                ) : (
                                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500 inline" />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {result.channel_groups?.length === 0 && (
              <div className="rounded-lg border p-4 text-center text-sm text-muted-foreground">
                No invoiceable bookings for {result.entity_name} in {result.period}.
              </div>
            )}

            {/* Unassigned properties section */}
            {result.unassigned_properties?.length > 0 && (
              <div className="rounded-lg border border-amber-300 dark:border-amber-800">
                <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-300 dark:border-amber-800">
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    Unassigned FR Properties (excluded from invoicing)
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    These properties have no explicit entity assignment and default to bi_sas.
                    They will NOT appear in the invoiceable set. Assign them to bi_immo or afpv if they should be invoiced.
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left px-2 py-1 font-medium">Property ID</th>
                        <th className="text-left px-2 py-1 font-medium">Name</th>
                        <th className="text-left px-2 py-1 font-medium">Default entity</th>
                        <th className="text-right px-2 py-1 font-medium">Active reservations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.unassigned_properties.map((p) => (
                        <tr key={p.property_id} className="border-b">
                          <td className="px-2 py-1 font-mono">{p.property_id}</td>
                          <td className="px-2 py-1">{p.property_name}</td>
                          <td className="px-2 py-1">
                            <Badge variant="outline" className="text-amber-600 border-amber-400">
                              {p.defaulted_entity_id}
                            </Badge>
                          </td>
                          <td className="px-2 py-1 text-right tabular-nums">{p.active_reservation_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Not-routable bookings section */}
            {hasNotRoutable && (
              <div className="rounded-lg border border-red-300 dark:border-red-800">
                <div className="p-3 bg-red-50 dark:bg-red-950/30 border-b border-red-300 dark:border-red-800">
                  <p className="text-sm font-medium flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    Not Routable ({result.not_routable.count} booking{result.not_routable.count !== 1 ? 's' : ''}, needs entity setup)
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    These bookings have an entity_id that does not resolve to any FR invoicing entity.
                    They are invisible in the invoiceable set. Register the missing entity in ENTITY_DEFS or reassign the property.
                  </p>
                </div>
                {(result.not_routable.groups || []).map((group) => (
                  <div key={group.entity_id} className="border-b last:border-b-0">
                    <div className="px-3 py-2 bg-red-50/50 dark:bg-red-950/20 flex items-center gap-2">
                      <Badge variant="outline" className="text-red-600 border-red-400 font-mono">
                        {group.entity_id}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {group.booking_count} booking{group.booking_count !== 1 ? 's' : ''}
                      </span>
                      {!group.registered_in_config && (
                        <Badge variant="destructive" className="text-[10px] px-1 py-0">
                          not in ENTITY_DEFS
                        </Badge>
                      )}
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/30">
                            <th className="text-left px-2 py-1 font-medium">ID</th>
                            <th className="text-left px-2 py-1 font-medium">Property</th>
                            <th className="text-left px-2 py-1 font-medium">Guest</th>
                            <th className="text-left px-2 py-1 font-medium">Check-in</th>
                            <th className="text-right px-2 py-1 font-medium">Net</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.bookings.map((bk) => (
                            <tr key={bk.uplisting_id} className="border-b">
                              <td className="px-2 py-1 font-mono">{bk.uplisting_id}</td>
                              <td className="px-2 py-1 max-w-[160px] truncate" title={bk.property_name}>
                                {bk.property_name}
                              </td>
                              <td className="px-2 py-1 max-w-[120px] truncate">{bk.guest_name}</td>
                              <td className="px-2 py-1">{bk.check_in}</td>
                              <td className="px-2 py-1 text-right tabular-nums">{fmtEUR(bk.net_revenue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
